import { useCallback, useEffect, useState } from "react";
import { ISelectOptions } from "../../../types/inputs";
import { Banco } from "../../../services/Banco";
import { Motivo } from "../../../services/Motivo";
import { TipoMotivoEnum } from "../../../enums/motivo";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { maskCnpj, maskCpf, removeDigitos } from "../../../utils/masks";
import { useContextSite } from "../../../context/Context";
import { Agendamento } from "../../../services/Agendamento";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { IReembolsoProps } from "../../../types/agendamento";

export const useRefundForm = () => {
  const fileTypes = ["JPG", "PNG", "GIF", "pdf"];
  const [bancoOptions, setbancoOptions] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const navigate = useNavigate();
  const [motivosOptions, setMotivosOptions] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const { setIsLoad } = useContextSite();
  const { uuidAgendamento } = useParams();

  const dataSchema = z
    .object({
      uuidMotivo: z.string().min(1, "Selecione o motivo"),
      outroMotivo: z.string().optional().optional().or(z.literal("")),
      nome: z.string().min(1, "Nome é obrigatório"),
      cpfCnpj: z.string().min(14, "CPF/CNPJ invalido"),
      codigoDoBanco: z.string().min(1, "Banco é obrigatório"),
      agencia: z.string().min(1, "Agência é obrigatório"),
      conta: z.string().min(1, "Conta é obrigatório"),
      operacao: z.string().optional().or(z.literal("")),
    })
    .refine(
      (data) =>
        motivosOptions.find((i) => i.value === data.uuidMotivo)?.label !==
          "Outro" ||
        (motivosOptions.find((i) => i.value === data.uuidMotivo)?.label ===
          "Outro" &&
          !!data.outroMotivo.trim()),
      {
        message: "A descricao do motivo é obrigatória",
        path: ["outroMotivo"],
      }
    );

  const schema = z.object({
    uuid: z.string().min(1, "uuid é obrigatório"),
    data: dataSchema,
    file: z.custom<File>((value) => value instanceof File, {
      message: "O arquivo deve ser um tipo válido",
    }),
  });

  const getBancos = useCallback(async () => {
    Banco.list().then(({ data }) => {
      const options = data.map((i) => ({ value: i.codigo, label: i.nome }));
      setbancoOptions(options);
    });
  }, []);

  const getMotivos = useCallback(async () => {
    Motivo.list({ tipo: TipoMotivoEnum.REEMBOLSO }).then(({ data }) => {
      const options = data.content.map((i) => ({
        value: i.uuid,
        label: i.descricao,
      }));
      setMotivosOptions(options);
    });
  }, []);

  useEffect(() => {
    getBancos();
    getMotivos();
  }, []);

  const {
    handleSubmit,
    register,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<IReembolsoProps>({
    defaultValues: {
      uuid: uuidAgendamento,
      data: {
        uuidMotivo: "",
        outroMotivo: "",
        nome: "",
        cpfCnpj: "",
        codigoDoBanco: "",
        agencia: "",
        conta: "",
        operacao: "",
      },
      file: undefined,
    },
    resolver: zodResolver(schema),
    criteriaMode: "all",
  });

  useEffect(() => {
    setValue("data.outroMotivo", "");
  }, [watch("data.uuidMotivo")]);

  useEffect(() => {
    if (watch("data.cpfCnpj")?.length <= 14) {
      setValue("data.cpfCnpj", maskCpf(watch("data.cpfCnpj")));
      return;
    }

    if (watch("data.cpfCnpj")?.length > 14) {
      setValue("data.cpfCnpj", maskCnpj(watch("data.cpfCnpj")));
    }
  }, [watch("data.cpfCnpj")]);

  function submitForm(data: IReembolsoProps) {
    setIsLoad(true);

    const PAYLOAD: IReembolsoProps = {
      ...data,
      data: {
        ...data.data,
        cpfCnpj: removeDigitos(data.data.cpfCnpj),
      },
    };

    Agendamento.reembolsar(PAYLOAD)
      .then(() => {
        toast.success("Solicitacao de cancelamento enviada com sucesso!");
        navigate("/agendamento");
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => setIsLoad(false));
  }

  return {
    fileTypes,
    bancoOptions,
    motivosOptions,
    handleSubmit,
    register,
    watch,
    control,
    Controller,
    submitForm,
    errors,
  };
};
